import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import css from '../../summary-view.module.css';
import SummaryGraph from '../../summary-graph';
import moment from 'moment';
import { AnnualYieldEntry } from '../../../../../interfaces/livestock-summary';

interface OptionDataMap {
  [key: string]: AnnualYieldEntry[];
}

interface SummaryData {
  herdCow: AnnualYieldEntry[];
  oldCow: AnnualYieldEntry[];
  firstMilk: AnnualYieldEntry[];
}


export default function AnnualYieldSummary({
  summaryData,
  title,
  loading
}: {
  summaryData: SummaryData;
  title: string;
  loading: boolean;
}) {

  const { t } = useTranslation();
  const [selectedOption, setSelectedOption] = useState('cow_in_herd');
  const [tableRows, setTableRows] = useState([]);

  const getOptionData = (): OptionDataMap => ({
    cow_first_milk: summaryData.firstMilk,
    old_cow: summaryData.oldCow,
    cow_in_herd: summaryData.herdCow,
  });

  const optionData = useMemo(getOptionData, [summaryData.firstMilk, summaryData.oldCow, summaryData.herdCow]);

  const updateTableRows = useCallback((selectedOption: string) => {
    if (!loading) {
      if (title === t('pages.livestock.summary.header.summary_produce_year')) {
        const selectedData = optionData[selectedOption];

        const keys = Object.keys(selectedData[0]);
        const lactationProduceCurrent = selectedData[selectedData.length - 1][keys[1]];
        const lactationProducePrev = selectedData[selectedData.length - 2][keys[1]];
        const lactationYearCurrent = selectedData[selectedData.length - 1][keys[2]];
        const lactationYearPrev = selectedData[selectedData.length - 2][keys[2]];

        const lactationProducePercentageDifference = (lactationProducePrev !== 0 && lactationProduceCurrent !== 0) ? (((lactationProduceCurrent - lactationProducePrev) / lactationProducePrev) * 100).toFixed(2) : 0;
        const lactationYearPercentageDifference = (lactationYearPrev !== 0 && lactationYearCurrent !== 0) ? (((lactationYearCurrent - lactationYearPrev) / lactationYearPrev) * 100).toFixed(2) : 0;

        const tableRows = [
          <tr>
            <td>{t('pages.livestock.summary.view.' + selectedOption)}</td>
            <td>{moment(selectedData[selectedData.length - 1][keys[0]]).format('MM.YYYY')}</td>
            <td>{moment(selectedData[selectedData.length - 2][keys[0]]).format('MM.YYYY')}</td>
            <td>{t('pages.livestock.summary.view.difference_percentage')}</td>
          </tr>,

          <tr>
            <td>{t('pages.livestock.summary.view.lactation_produce')}</td>
            <td>{lactationProduceCurrent}</td>
            <td>{lactationProducePrev}</td>
            <td>{lactationProducePercentageDifference}%</td>
          </tr>,

          <tr>
            <td>{t('pages.livestock.summary.view.lactation_produce_year')}</td>
            <td>{lactationYearCurrent}</td>
            <td>{lactationYearPrev}</td>
            <td>{lactationYearPercentageDifference}%</td>
          </tr>
        ];

        setTableRows(tableRows);
      }
    }
  }, [t, loading, title, optionData]);

  const handleOptionChange = (event: any) => {
    const value = event.target.value;
    setSelectedOption(value);
  };

  const prevSelectedOptionRef = useRef(selectedOption);
  const [hasInitialized, setHasInitialized] = useState(false);

  useEffect(() => {
    if (!loading && !hasInitialized) {
      updateTableRows(selectedOption);
      prevSelectedOptionRef.current = selectedOption;
      setHasInitialized(true);
    } else if (!loading && prevSelectedOptionRef.current !== selectedOption) {
      updateTableRows(selectedOption);
      prevSelectedOptionRef.current = selectedOption;
    }
  }, [loading, selectedOption, updateTableRows, hasInitialized]);

  return (
    <div className={css['summary-container']}>
      <div className={css['left-panel']}>
        {Object.keys(optionData).map((option) => (
          <label key={option}>
            <input
              type="radio"
              name="option"
              value={option}
              checked={selectedOption === option}
              onChange={handleOptionChange}
            />
            {t('pages.livestock.summary.view.' + option)}
          </label>
        ))}
        <div className={css['grid-container-yield']}>
          <SummaryGraph
            title={title}
            height={193}
            xKey="current_date"
            dataObjects={[
              {
                dataKey: "last_control",
                dataTitle: t('pages.livestock.summary.view.lactation_produce'),
                strokeColor: "#12589F",
                yAxis: "third"
              },
              {
                dataKey: "yearYield",
                dataTitle: t('pages.livestock.summary.view.lactation_produce_year'),
                strokeColor: "#50AE2F",
                yAxis: "left"
              },
            ]}
            data={optionData[selectedOption]}
          />
          <p className={css['summary']}>{t('pages.livestock.summary.view.year_average')}</p>

          <table className={css['under-table']}>
            <tbody className={css['strong']}>
              {tableRows}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
